import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { HighScore, HighScoreScope } from '../types/game';
import {
  getHighScoreSet,
  MAX_LOCAL_HIGHSCORES,
  MAX_GLOBAL_HIGHSCORES,
  syncGlobalHighScores
} from '../utils/highScore';

interface HighScoreListProps {
  isVisible: boolean;
  onClose: () => void;
  modeId?: string; // classic, maxi, etc
}

const Overlay = styled.div<{ $isVisible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${props => props.$isVisible ? 'flex' : 'none'};
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 12px;
  padding: 30px;
  max-width: 500px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const Title = styled.h2`
  margin: 0;
  color: #333;
  font-size: 24px;
`;

const MetaRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 16px;
  font-size: 13px;
  color: #555;
`;

const ScopeToggle = styled.div`
  display: inline-flex;
  gap: 8px;
  align-items: center;
`;

const ScopeButton = styled.button<{ $active: boolean }>`
  background: ${props => props.$active ? '#2196F3' : 'transparent'};
  color: ${props => props.$active ? 'white' : '#2196F3'};
  border: 2px solid #2196F3;
  border-radius: 999px;
  padding: 6px 16px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.$active ? '#1976D2' : 'rgba(33, 150, 243, 0.1)'};
  }
`;

const InfoNote = styled.div`
  background: #e3f2fd;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 13px;
  color: #0d47a1;
  margin-bottom: 16px;
  line-height: 1.4;
`;

const CloseButton = styled.button`
  background: #f44336;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  cursor: pointer;
  font-size: 14px;
  
  &:hover {
    background: #d32f2f;
  }
`;

// CHANGE (2026-02-23): "Rensa"-knappar tas bort enligt krav. (ClearButton borttagen.)

const ScoreList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ScoreItem = styled.div<{ rank: number }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: ${props => {
    if (props.rank === 1) return '#FFD700';
    if (props.rank === 2) return '#C0C0C0';
    if (props.rank === 3) return '#CD7F32';
    return '#f5f5f5';
  }};
  border-radius: 8px;
  border-left: 4px solid ${props => {
    if (props.rank === 1) return '#FFA000';
    if (props.rank === 2) return '#757575';
    if (props.rank === 3) return '#8D6E63';
    return '#2196F3';
  }};
`;

const PlayerInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Rank = styled.div`
  font-weight: bold;
  font-size: 18px;
  min-width: 30px;
  color: #333;
`;

const PlayerName = styled.div`
  font-weight: bold;
  color: #333;
`;

const ScoreInfo = styled.div`
  text-align: right;
`;

const Score = styled.div`
  font-weight: bold;
  font-size: 18px;
  color: #333;
`;

const ScoreDate = styled.div`
  font-size: 12px;
  color: #666;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #666;
  font-style: italic;
`;

const HighScoreList: React.FC<HighScoreListProps> = ({ isVisible, onClose, modeId = 'classic' }) => {
  const [highScores, setHighScores] = useState<HighScore[]>([]);
  const [scope, setScope] = useState<HighScoreScope>('local');
  const [updatedAt, setUpdatedAt] = useState<string | undefined>();
  const [isLoadingGlobal, setIsLoadingGlobal] = useState(false);

  const loadScores = useCallback(async (targetScope: HighScoreScope, isCancelled?: () => boolean) => {
    const cachedBundle = getHighScoreSet(modeId);
    if (isCancelled?.()) return;
    setUpdatedAt(cachedBundle.updatedAt);
    setHighScores(targetScope === 'global' ? cachedBundle.global : cachedBundle.local);

    if (targetScope === 'global') {
      if (isCancelled?.()) return;
      setIsLoadingGlobal(true);
      try {
        const syncedBundle = await syncGlobalHighScores(modeId);
        if (isCancelled?.()) return;
        setUpdatedAt(syncedBundle.updatedAt);
        setHighScores(syncedBundle.global);
      } finally {
        if (!isCancelled?.()) {
          setIsLoadingGlobal(false);
        }
      }
    }
  }, [modeId]);

  useEffect(() => {
    if (isVisible) {
      let cancelled = false;
      void loadScores(scope, () => cancelled);
      return () => {
        cancelled = true;
      };
    }
  }, [isVisible, scope, loadScores]);

  useEffect(() => {
    if (!isVisible) {
      setScope('local');
    }
  }, [isVisible]);

  const formattedUpdatedAt = useMemo(() => {
    if (!updatedAt) return undefined;
    const date = new Date(updatedAt);
    if (Number.isNaN(date.getTime())) return undefined;
    return date.toLocaleString('sv-SE', { hour12: false });
  }, [updatedAt]);

  const capacity = scope === 'global' ? MAX_GLOBAL_HIGHSCORES : MAX_LOCAL_HIGHSCORES;
  const scopeLabel = scope === 'global' ? 'Global' : 'Lokal';
  const scopeEmoji = scope === 'global' ? '🌍' : '🏡';

  // CHANGE (2026-02-23): Logik för att rensa highscores borttagen från UI enligt krav.

  if (!isVisible) return null;

  return (
    <Overlay $isVisible={isVisible} onClick={onClose}>
      <ModalContainer onClick={e => e.stopPropagation()}>
        <Header>
          <Title>
            🏆 Highscore {
              modeId.startsWith('daily:')
                ? `— Dagens (${modeId.split(':')[1]})`
                : modeId === 'maxi' ? '— Maxi' : ''
            }
          </Title>
          <CloseButton onClick={onClose}>Stäng</CloseButton>
        </Header>

        <MetaRow>
          <ScopeToggle>
            <ScopeButton $active={scope === 'local'} onClick={() => setScope('local')}>
              🏡 Lokal
            </ScopeButton>
            <ScopeButton $active={scope === 'global'} onClick={() => setScope('global')}>
              🌍 Global
            </ScopeButton>
          </ScopeToggle>
          <div>
            {scopeEmoji} {scopeLabel}: {highScores.length}/{capacity}
            {formattedUpdatedAt ? ` • Uppdaterad ${formattedUpdatedAt}` : ''}
          </div>
        </MetaRow>

        {scope === 'global' && (
          <InfoNote>
            🌍 Den globala listan sparar de {MAX_GLOBAL_HIGHSCORES} bästa resultaten per spelläge.
            {isLoadingGlobal ? ' Synkar med servern...' : ''}
          </InfoNote>
        )}
        
        {highScores.length === 0 ? (
          <EmptyState>
            Inga highscores än! Spela ditt första spel för att komma på listan.
          </EmptyState>
        ) : (
          <>
            <ScoreList>
              {highScores.map((score, index) => (
                <ScoreItem key={score.id} rank={index + 1}>
                  <PlayerInfo>
                    <Rank>#{index + 1}</Rank>
                    <PlayerName>{score.playerName}</PlayerName>
                  </PlayerInfo>
                  <ScoreInfo>
                    <Score>{score.score} poäng</Score>
                    <ScoreDate>{score.date}</ScoreDate>
                  </ScoreInfo>
                </ScoreItem>
              ))}
            </ScoreList>
            
            {/* CHANGE (2026-02-23): "Rensa lokal lista" borttagen enligt krav. */}
          </>
        )}
      </ModalContainer>
    </Overlay>
  );
};

export default HighScoreList;
